import axios from 'axios'
import axiosZS from 'utils/axiosZS'
import { createHeader } from 'utils/header-zs'
import { STORAGE_TYPE } from '../../constants/constantAkamai'
import { 
  DEFAULT_PAGE_SIZE,
  STORAGE_ENDPOINT,
  ALLOWED_EXTENSIONS_ENDPOINT,
  SECURE_LINK_ENDPOINT,
  SECURE_LINK_FILES,
  BULK_DELETE_ENDPOINT,
  API_VERSION_V1,
  DEFAULT_TIMEOUT_MS,
  ERROR_CODES
 } from 'views/app-views/my-files/utils/constant'       // EDCL-350 (RS)

const handleApiError = (err) => {
  const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
  const statusCode = err.response?.status;

  return { errorMessage, statusCode }
}

export const initializeAkamaiService = async (tenantId) => {
  const header = await createHeader()
  return new Promise(function (resolve, reject) {
    return axiosZS
      .post(`/storage/v1/${STORAGE_TYPE}/bucket`, {}, header)
      .then((res) => {
        console.log('done updating')
        resolve(res)
      })
      .catch((err) => {
        console.log('Error while updating: ', err.message)
        resolve({ status: 'error', result: err.message })
      })
  })
}

export const akamaiServiceStatus = async () => {
  const header = await createHeader()
  return new Promise(function (resolve, reject) {
    return axiosZS
      .get(`/storage/v1/${STORAGE_TYPE}/status`, header)
      .then((res) => {
        console.log('done updating')
        resolve(res)
      })
      .catch((err) => {
        console.log('Error while updating: ', err.message)
        resolve({ status: 'error', result: err.message })
      })
  })
}

export const deleteFile = async (body) => {
  const header = await createHeader()
  return new Promise(function (resolve, reject) {
    return axiosZS
      .delete(`/storage/v1/${STORAGE_TYPE}/${body?.folderName}/${body?.fileName}`, header)
      .then((res) => {
        console.log('done updating')
        resolve(res)
      })
      .catch((err) => {
        console.log('Error while updating: ', err.message)
        resolve({ status: 'error', result: err.message })
      })
  })
}

export const listFile = async (body) => {
  const header = await createHeader()
  return new Promise(function (resolve, reject) {
    return axiosZS
      .get(`/storage/v1/${STORAGE_TYPE}/${body?.folderName}`, header)
      .then((res) => {
        console.log('done updating')
        resolve(res)
      })
      .catch((err) => {
        console.log('Error while updating: ', err.message)
        resolve({ status: 'error', result: err.message })
      })
  })
}

// EDCL-350 (RS) ->
/**
 * Fetches a paginated list of files from the given storage folder.
 * Endpoint: GET /storage/{version}/{STORAGE_TYPE}/{folderName}?limit&offset&nextToken&fileName&totalFiles
 * @param {Object} body
 * @param {string} body.folderName
 * @param {string} [body.fileName]
 * @param {number} [body.limit]
 * @param {number} [body.offset]
 * @param {string} [body.nextToken]
 * @param {number} [body.totalFiles]
 * @returns {Promise<Object>} Success or { status: 'error', statusCode, result } on failure.
 */
export const listFileWithPagination = async (body) => {
  const header = await createHeader()
  const folderName = body?.folderName;
  const fileName = body?.fileName; // Optional search parameter - only include if provided
  const limit = body?.limit || DEFAULT_PAGE_SIZE;
  const totalFiles = body?.totalFiles; // Optional pagination parameter - only include if provided
  const offset = body?.offset || 0;
  const nextToken = body?.nextToken || '';
  
  // Build query params dynamically - include only when values are provided
  let queryParams = `?limit=${limit}&offset=${offset}`;
  if (nextToken) {
    queryParams += `&nextToken=${nextToken}`;
  }
  if (fileName) {
    queryParams += `&fileName=${encodeURIComponent(fileName)}`;
  }
  if (totalFiles) {
    queryParams += `&totalFiles=${totalFiles}`;
  }

  return new Promise(function (resolve) {
    return axiosZS
      .get(`${STORAGE_ENDPOINT}/${STORAGE_TYPE}/${folderName}${queryParams}`, { ...header, timeout: DEFAULT_TIMEOUT_MS })
      .then((res) => {
        resolve(res)
      })
      .catch((err) => {
        // Consistent error handling with status code
        const { errorMessage, statusCode } = handleApiError(err);
        console.error(`Error fetching files (${statusCode}):`, errorMessage);
        resolve({ status: 'error', statusCode, result: errorMessage })
      })
  })
}

export const getAllowedFileTypeList = async (body) => {
  const header = await createHeader()
  const folderName = body?.folderName;

  return new Promise(function (resolve) {
    return axiosZS
      .get(`${STORAGE_ENDPOINT}/${STORAGE_TYPE}/${folderName}/${ALLOWED_EXTENSIONS_ENDPOINT}`, header)
      .then((res) => {
        resolve(res)  
      })
      .catch((err) => {
        // Consistent error handling with status code
        const { errorMessage, statusCode } = handleApiError(err);
        console.error(`Error fetching file types (${statusCode}):`, errorMessage);
        resolve({ status: 'error', statusCode, result: errorMessage })
      })
  })
}

export const getFileManagerConfigs = async () => {
  const header = await createHeader()
  
  return new Promise(function (resolve) {
    return axiosZS
      .get(`${STORAGE_ENDPOINT}/${STORAGE_TYPE}/configs/filemanager`, header)
      .then((res) => {
        resolve(res)  
      })
      .catch((err) => {
        // Consistent error handling with status code
        const { errorMessage, statusCode } = handleApiError(err);
        console.error(`Error fetching file types (${statusCode}):`, errorMessage);
        resolve({ status: 'error', statusCode, result: errorMessage })
      })
  })
}

export const generateSecureLink = async (body) => {
  const header = await createHeader()
  const folderName = body?.folderName;
  // btoa only supports Latin1, so UTF-8 encode first to support any Unicode filename
  const rawName = body?.fileName?.split("/").pop() || '';
  const fileName = encodeURIComponent(btoa(encodeURIComponent(rawName)));

  return new Promise(function (resolve) {
    return axiosZS
      .get(`${STORAGE_ENDPOINT}/${STORAGE_TYPE}/${SECURE_LINK_FILES}/${folderName}/${fileName}/${SECURE_LINK_ENDPOINT}`, header)
      .then((res) => {
        resolve(res)
      })
      .catch((err) => {
        // Consistent error handling with status code
        const { errorMessage, statusCode } = handleApiError(err);
        console.error(`Error generating secure link (${statusCode}):`, errorMessage);
        resolve({ status: 'error', statusCode, result: errorMessage })
      })
  })
}

export const deleteFiles = async (body) => {
  const header = await createHeader()
  return new Promise(function (resolve) {
    return axiosZS
      .post(`${STORAGE_ENDPOINT}/${STORAGE_TYPE}/${BULK_DELETE_ENDPOINT}`, body, header)
      .then((res) => {
        resolve(res)
      })
      .catch((err) => {
        // Consistent error handling with status code
        const { errorMessage, statusCode } = handleApiError(err);
        console.error(`Error deleting files (${statusCode}):`, errorMessage);
        resolve({ status: 'error', statusCode, result: errorMessage })
      })
  })
}

// <- EDCL-350 (RS)


export const downloadFile = async (body) => {
  const header = await createHeader()
  return new Promise(function (resolve, reject) {
    return axiosZS
      .get(
        `/storage/v1/${STORAGE_TYPE}/${body?.folderName}/${body?.fileName}/${body?.lifetime}`,
        header
      )
      .then((res) => {
        console.log('done updating')
        resolve(res)
      })
      .catch((err) => {
        console.log('Error while updating: ', err.message)
        resolve({ status: 'error', result: err.message })
      })
  })
}

export const accessLinkFile = async (body) => {
  const header = await createHeader()
  return new Promise(function (resolve, reject) {
    return axiosZS
      .get(
        `/storage/v1/${STORAGE_TYPE}/${body?.folderName}/${body?.fileName}/${body?.lifetime}?stream=true`,
        header
      )
      .then((res) => {
        console.log('done accessable link')
        console.log(res)
        resolve(res)
      })
      .catch((err) => {
        console.log('Error while creating accessable link: ', err.message)
        resolve({ status: 'error', result: err.message })
      })
  })
}
// EDCL-350 (RS) -> 
export const getUploadURL = async (body, apiVersion = API_VERSION_V1) => {
  const header = await createHeader()
  return new Promise(function (resolve, reject) {
    return axiosZS
      .post(`/storage/${apiVersion}/${STORAGE_TYPE}/${body?.folderName}`, body, header)
      .then((res) => {
        console.log('done updating')
        resolve(res)
      })
      .catch((err) => {
        const { errorMessage, statusCode } = handleApiError(err);
        console.error(`Error (${statusCode}): ${errorMessage}`);
        resolve({ status: 'error', statusCode, result: errorMessage })
      })
  })
}
// EDCL-350 (RS) <-

export const uploadFile = async (url, fileContent, ftype) => {
  return new Promise(function (resolve, reject) {
    return axios
      .put(url, fileContent, {
        headers: {
          'Content-Type': ftype,
          'x-amz-meta-type': ftype,
        }
      })
      .then((res) => {
        resolve(res)
      })
      .catch((err) => {
        console.log('Error while updating: ', err.message)
        const errorMessage = err.code === ERROR_CODES.TIMEOUT ? 'Upload timed out. Please check your network connection and try again.' : err.message;
        resolve({ status: 'error', result: errorMessage })
      })
  })
}

export const getStorageInfo = async (folderName) => {
    const URL = folderName? `/storage/v1/${STORAGE_TYPE}/usage/upload?folderName=${folderName}`: `/storage/v1/${STORAGE_TYPE}/usage/upload`
    const header = await createHeader()
    return new Promise(function (resolve, reject) {
        return axiosZS
            .get(URL, header)
            .then((res) => {
                console.log('Got the limit')
                resolve(res)
            })
            .catch((err) => {
                console.log('Error while getting storage limit: ', err.message)
                resolve({ status: 'error', result: err.message })
            })
    })
}

export const getStorageInfoDownload = async () => {
  const header = await createHeader()
  return new Promise(function (resolve, reject) {
    return axiosZS
      .get(`/storage/v1/${STORAGE_TYPE}/usage/download`, header)
      .then((res) => {
        console.log('Got the limit')
        resolve(res)
      })
      .catch((err) => {
        console.log('Error while getting storage limit: ', err.message)
        resolve({ status: 'error', result: err.message })
      })
  })
}
